<script setup lang="ts">
import { computed } from "vue";
import { Link, usePage } from "@inertiajs/vue3";
import { route } from "ziggy-js";

const page = usePage<any>();

const rubRate = computed(() => page.props.roblox_rates?.rub ?? "1,5");
const usdRate = computed(() => page.props.roblox_rates?.usd ?? "120");
const isRu = computed(() => page.props.lang === "ru");

/**
 * Related content-cluster articles. Kept RU-only for now: these are the pages
 * rewritten to the structured-article standard (see docs/seo/article-writing.md),
 * and only their Russian versions exist for every slug in the list.
 */
const relatedArticles = [
    {
        slug: "roblox-razblokirovali-v-rossii-kak-kupit-robuksy",
        title: "Roblox разблокировали в России: как купить робуксы в 2026 году",
    },
    {
        slug: "skolko-stoyat-robuksy-2026-ceny-i-kursy",
        title: "Сколько стоят робуксы в 2026 году: цены и курсы",
    },
    {
        slug: "populyarnye-igry-roblox-2026-onlayn-i-robuksy",
        title: "Самые популярные игры Roblox в 2026 году",
    },
    {
        slug: "rbkstri",
        title: "Почему «бесплатных робуксов» не бывает",
    },
];
</script>

<template>
    <section
        class="mt-12 w-full px-5 pb-4 text-slate-200 font-sans max-w-[820px] m-auto"
    >
        <h2
            class="font-['Unbounded'] text-white text-xl sm:text-2xl font-medium mb-5"
        >
            {{ $t("How to buy Robux on AnyKod") }}
        </h2>

        <p class="text-base leading-relaxed mb-4">
            {{
                $t(
                    "AnyKod is a marketplace where you can buy Robux for Roblox without a VPN and without a foreign card. Enter how much you want to pay or how many Robux you want to receive, pay for the order, and we transfer the Robux to your account through a Game Pass — the same mechanism Roblox itself uses for payouts to creators."
                )
            }}
        </p>

        <h3 class="font-['Unbounded'] text-white text-lg font-medium mt-8 mb-3">
            {{ $t("Order in four steps") }}
        </h3>
        <ol class="list-decimal pl-5 flex flex-col gap-2 text-base mb-4">
            <li>
                {{
                    $t(
                        "Enter the amount in the calculator at the top of the page — the rate is recalculated instantly."
                    )
                }}
            </li>
            <li>
                {{
                    $t(
                        "Specify your Roblox nickname and choose the game where the Game Pass will be created."
                    )
                }}
            </li>
            <li>
                {{
                    $t(
                        "Create a Game Pass with the price we show, and pay for the order in a way that suits you."
                    )
                }}
            </li>
            <li>
                {{
                    $t(
                        "We buy your Game Pass — the Robux appear in your Roblox transactions right away, in the Pending Robux line."
                    )
                }}
            </li>
        </ol>

        <h3 class="font-['Unbounded'] text-white text-lg font-medium mt-8 mb-3">
            {{ $t("Why delivery takes 5-7 days") }}
        </h3>
        <p class="text-base leading-relaxed mb-4">
            {{
                $t(
                    "This is not our delay — it is a Roblox rule. Any Robux received through a Game Pass sits in Pending for 5-7 days before it becomes available to spend. It works exactly the same for game developers. You will see the amount in your transactions immediately after we pay, so you can verify the order has been fulfilled without waiting."
                )
            }}
        </p>

        <h3 class="font-['Unbounded'] text-white text-lg font-medium mt-8 mb-3">
            {{ $t("Rate and minimum order") }}
        </h3>
        <p class="text-base leading-relaxed mb-4">
            <span v-if="isRu">
                Текущий курс — 1 ₽ = {{ rubRate }} Robux. Минимальный заказ —
                {{ $page.props.min_roblox_order_amount ?? 20 }} робуксов, так
                что можно сначала взять небольшую сумму и убедиться, что всё
                работает, прежде чем покупать больше.
            </span>
            <span v-else>
                The current rate is 1 $ = {{ usdRate }} Robux. The minimum order
                is {{ $page.props.min_roblox_order_amount ?? 20 }} Robux, so you
                can start with a small purchase and make sure everything works
                before buying more.
            </span>
        </p>

        <h3 class="font-['Unbounded'] text-white text-lg font-medium mt-8 mb-3">
            {{ $t("Is it safe") }}
        </h3>
        <p class="text-base leading-relaxed mb-4">
            {{
                $t(
                    "We never ask for your Roblox password or your account cookie — the Game Pass mechanism does not require them. Anyone asking for your login details to “top up Robux” is trying to steal the account. We earn by selling Robux, not by scamming buyers, so every order gets fulfilled."
                )
            }}
        </p>

        <h3 class="font-['Unbounded'] text-white text-lg font-medium mt-8 mb-3">
            {{ $t("What about free Robux generators") }}
        </h3>
        <p class="text-base leading-relaxed mb-4">
            {{
                $t(
                    "They do not exist. Robux is issued only by Roblox itself, and no third-party site can generate it. Everything advertised as a “Robux generator” is either a phishing page collecting account credentials or an endless chain of surveys. The only free Robux worth chasing are the giveaways we run in our own Telegram group."
                )
            }}
        </p>

        <template v-if="isRu">
            <h3
                class="font-['Unbounded'] text-white text-lg font-medium mt-8 mb-3"
            >
                {{ $t("Read more") }}
            </h3>
            <ul class="list-disc pl-5 flex flex-col gap-2 text-base">
                <li v-for="article in relatedArticles" :key="article.slug">
                    <Link
                        :href="
                            route('product-page.show', {
                                productPage: article.slug,
                            })
                        "
                        class="text-sky-200 underline hover:opacity-70 transition-opacity"
                    >
                        {{ article.title }}
                    </Link>
                </li>
            </ul>
        </template>
    </section>
</template>
